// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// One rack row: the row footprint, its rack cells and its label. Rack cells
// are drawn from `racks` (a count) rather than per-rack records — when the
// twin publishes rack-level assets this is where they bind, one <rect> each.

import React from 'react';
import type { PlanRowBlock } from '../../../types/floorPlan';

const LABEL_GAP = 6;

export default function PlanRackRow({
  row,
  onSelect,
}: {
  row: PlanRowBlock;
  onSelect?: (_rowId: string) => void;
}) {
  const cells = row.future ? 0 : (row.racks ?? 0);
  const pitch = cells > 0 ? row.h / cells : 0;
  const interactive = Boolean(onSelect) && !row.future;

  return (
    <g
      className={`plan-row${row.future ? ' plan-row-future' : ''}${
        interactive ? ' plan-row-interactive' : ''
      }`}
      data-testid={`plan-row-${row.id}`}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? row.label : undefined}
      onClick={interactive ? () => onSelect?.(row.id) : undefined}
      onKeyDown={
        interactive
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(row.id);
              }
            }
          : undefined
      }
    >
      <rect x={row.x} y={row.y} width={row.w} height={row.h} className="plan-row-fill" />
      {Array.from({ length: cells }, (_, i) => (
        <rect
          key={i}
          x={row.x}
          y={row.y + i * pitch}
          width={row.w}
          height={pitch}
          className="plan-row-cell"
        />
      ))}
      <text
        x={row.x + row.w / 2}
        y={row.y - LABEL_GAP}
        className="plan-row-label"
        textAnchor="middle"
      >
        {row.future ? `(F) ${row.label}` : row.label}
      </text>
    </g>
  );
}
